import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { Menu, MenuItem, CircularProgress, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useRecordContext, useUpdate, useNotify, useRefresh, usePermissions, useGetIdentity } from "react-admin";
import { useState } from "react";
import { AlertType, ResourceName, Task, TaskStatus, TASK_STATUS_CHOICES } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { canActOnRecord } from "@/utils/permissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface EditableTaskStatusFieldProps {
  resource?: ResourceName;
  label?: string;
}

export const EditableTaskStatusField = ({ resource = ResourceName.TASKS }: EditableTaskStatusFieldProps) => {
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();

  const record = useRecordContext<Task>();
  const canEdit = canActOnRecord(permissions, ResourceName.TASKS, "edit", record, identity?.id as string | undefined);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const source = "status";

  if (!record) return null;

  const currentStatus = record[source];
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!canEdit) return; // Prevent opening menu if can't edit
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: TaskStatus, event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();

    if (newStatus === currentStatus) return;

    setIsUpdating(true);

    try {
      const { record: relatedRecord } = record;

      const updatePayload: Partial<Task> = {
        id: record.id,
        title: record.title,
        description: record.description,
        status: newStatus,
        completed: newStatus === TaskStatus.COMPLETED,
        due_date: record.due_date,
        assigned_user_id_ref: record.assigned_user_id_ref,
        display_claim_exhibit: record.display_claim_exhibit,
        comments: Array.isArray(record.comments) ? record.comments : [],
      };

      // Explicitly preserve the related record reference to prevent it from being erased
      if (relatedRecord && typeof relatedRecord === "object") {
        // Only include if it has meaningful data (not empty)
        if (relatedRecord.object_type || relatedRecord.object_id || relatedRecord.label) {
          updatePayload.record = {
            object_type: relatedRecord.object_type,
            object_id: relatedRecord.object_id,
            label: relatedRecord.label,
          };
        }
      }

      await update(
        resource,
        {
          id: record.id,
          data: updatePayload,
          previousData: record,
        },
        {
          onSuccess: () => {
            notify(`Status updated to ${formatToTitleCase(newStatus)}`, {
              type: AlertType.SUCCESS,
            });
            refresh();
          },
          onError: (error) => {
            notifyError(error);
          },
        }
      );
    } catch (error) {
      notifyError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isUpdating) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <StatusChipField source={source} />
      </Box>
    );
  }

  // Read-only mode: just show the chip without any interactive elements
  if (!canEdit) {
    return <StatusChipField source={source} />;
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          "&:hover .status-arrow": {
            opacity: 1,
          },
        }}
      >
        <StatusChipField source={source} />
        <KeyboardArrowDownIcon
          className="status-arrow"
          sx={{
            opacity: 0,
            transition: "opacity 0.2s",
            fontSize: "1.2rem",
            color: "text.secondary",
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {TASK_STATUS_CHOICES.map(({ id, name }) => (
          <MenuItem key={id} selected={id === currentStatus} onClick={(e) => handleStatusChange(id, e)}>
            {name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
